import type { MapLocation, LocationType } from '@/entities/dashboard/mapping';
import type { LocationDisplay } from './types';

export class LocationModel implements MapLocation {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly type: LocationType,
    public readonly lat: number,
    public readonly lng: number,
    public readonly siteId: string,
    public readonly regionId: string,
    public readonly subregionId: string,
    public readonly countryId: string,
    /** Ports only: 1 airport, 2 seaport, 3 helipad, 4 rail terminal, 5 river port. */
    public readonly portType?: number,
    /** Lifecycle flags — see `MapLocation`. Defaults describe a live location. */
    public readonly isDeleted = false,
    public readonly isLocked = false,
    public readonly isActive = true
  ) {}

  /**
   * Dashboard value for the active map data layer (stock / releases / requests).
   * Attached by `withData`, absent on a freshly loaded location.
   */
  readonly dataValue?: number;

  /** Weekly history behind `dataValue`, used by the time machine and the popup trend. */
  readonly dataProgress?: number[];

  static fromResponse(data: MapLocation): LocationModel {
    return new LocationModel(
      data.id,
      data.name,
      data.type,
      data.lat,
      data.lng,
      String(data.siteId),
      String(data.regionId),
      String(data.subregionId),
      String(data.countryId),
      data.portType,
      data.isDeleted ?? false,
      data.isLocked ?? false,
      data.isActive ?? true
    );
  }

  get displayName(): string {
    return this.name.replace(/\s*\(.*?\)\s*/g, '').trim();
  }

  get typeName(): string {
    const typeNames: Record<string, string> = {
      W: 'Warehouse',
      P: 'Port',
      C: 'Customer',
      S: 'Site'
    };
    return typeNames[this.type] || 'Unknown';
  }

  get coordinates(): [number, number] {
    return [this.lat, this.lng];
  }

  get icon(): string {
    const icons: Record<string, string> = {
      W: 'warehouse',
      P: 'truck',
      C: 'users',
      S: 'map-pin'
    };
    return icons[this.type] || 'map-pin';
  }

  get color(): string {
    const colors: Record<string, string> = {
      W: 'blue',
      P: 'green',
      C: 'orange',
      S: 'purple'
    };
    return colors[this.type] || 'gray';
  }

  /**
   * Returns a **new** location carrying the given data-layer values.
   *
   * Copying instead of mutating is what makes the map redraw: the markers are
   * memoised on the location object, so an in-place update would be invisible
   * to React and the map would keep showing the previous response (353351).
   */
  withData(dataValue: number, dataProgress: number[]): LocationModel {
    const next = new LocationModel(
      this.id,
      this.name,
      this.type,
      this.lat,
      this.lng,
      this.siteId,
      this.regionId,
      this.subregionId,
      this.countryId,
      this.portType
    );

    return Object.assign(next, { dataValue, dataProgress });
  }

  toDisplay(): LocationDisplay {
    return {
      id: this.id,
      name: this.displayName,
      fullName: this.name,
      type: this.type,
      typeName: this.typeName,
      coordinates: this.coordinates,
      icon: this.icon,
      color: this.color
    };
  }

  distanceTo(other: LocationModel): number {
    const R = 6371;
    const dLat = this.toRad(other.lat - this.lat);
    const dLon = this.toRad(other.lng - this.lng);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(this.lat)) * Math.cos(this.toRad(other.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return (deg * Math.PI) / 180;
  }
}
